import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUploadCloud, FiFileText, FiClipboard, FiDownload, FiLoader, FiAlertTriangle } from 'react-icons/fi';
import SectionTitle from '../components/SectionTitle';
import Card from '../components/Card';

const FilePreview = ({ path, content }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <Card className="mb-4 bg-gray-50 border border-gray-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left flex items-center justify-between"
      >
        <div className="flex items-center text-sm font-medium text-gray-700">
          <FiFileText className="mr-2 text-gray-500" />
          <span>{path}</span>
        </div>
        <span className="text-xs text-gray-500">
          {isExpanded ? 'Collapse' : 'Expand'}
        </span>
      </button>
      
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 border-t border-gray-200 pt-4"
        >
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-64 font-mono">
            <code>{content}</code>
          </pre>
        </motion.div>
      )}
    </Card>
  );
};

function CodeExtractor() {
  const [zipFile, setZipFile] = useState(null);
  const [extractedFiles, setExtractedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.type.includes('zip') && !file.name.endsWith('.zip')) {
      setError('Invalid file type. Please upload a .zip file.');
      return;
    }

    setError('');
    setZipFile(file);
    setIsLoading(true);

    try {
      // Simulate file extraction (in real app, would use JSZip)
      setTimeout(() => {
        const mockFiles = [
          { path: 'src/App.jsx', content: 'import React from "react";\n\nfunction App() {\n  return <div>Hello World</div>;\n}\n\nexport default App;' },
          { path: 'src/index.js', content: 'import React from "react";\nimport ReactDOM from "react-dom";\nimport App from "./App";\n\nReactDOM.render(<App />, document.getElementById("root"));' },
          { path: 'package.json', content: '{\n  "name": "my-app",\n  "version": "1.0.0",\n  "dependencies": {\n    "react": "^18.0.0"\n  }\n}' }
        ];
        setExtractedFiles(mockFiles);
        setIsLoading(false);
      }, 2000);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const buildConsolidatedText = () => {
    return extractedFiles
      .map(file => `// --- ${file.path} ---\n\n${file.content}\n\n`)
      .join('');
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(buildConsolidatedText());
    console.log('All code copied to clipboard');
  };

  const handleDownloadTxt = () => {
    const text = buildConsolidatedText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${zipFile.name.replace(/\.zip$/, '')}-code.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <SectionTitle 
        title="Code Extractor Tool" 
        subtitle="Upload a .zip file to extract and view all code files in a consolidated format." 
      />

      <Card>
        <label
          onDragOver={handleDragOver} 
          onDrop={handleDrop} 
          htmlFor="zip-upload"
          className="relative block w-full border-2 border-gray-300 border-dashed rounded-xl p-12 text-center hover:border-indigo-400 cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all duration-200"
        >
          <FiUploadCloud className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <span className="block text-lg font-medium text-gray-900 mb-2">
            {zipFile ? zipFile.name : 'Upload a .zip file'}
          </span>
          <span className="block text-sm text-gray-500">
            or drag and drop your zip file here
          </span>
          <input 
            id="zip-upload" 
            type="file" 
            accept=".zip,application/zip" 
            className="sr-only" 
            onChange={handleFileChange} 
          />
        </label>

        <AnimatePresence>
          {isLoading && (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="text-center py-8"
            >
              <FiLoader className="mx-auto h-8 w-8 text-indigo-600 animate-spin mb-4" />
              <p className="text-gray-600">Extracting files from {zipFile?.name}...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg"
          >
            <div className="flex">
              <FiAlertTriangle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </motion.div>
        )}

        {extractedFiles.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="mt-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Extracted {extractedFiles.length} files
              </h3>
              <div className="flex gap-3">
                <button 
                  onClick={handleCopyAll} 
                  className="btn-secondary"
                >
                  <FiClipboard className="mr-2 h-4 w-4" /> 
                  Copy All Code
                </button>
                <button 
                  onClick={handleDownloadTxt} 
                  className="btn-primary"
                >
                  <FiDownload className="mr-2 h-4 w-4" /> 
                  Download .txt
                </button>
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {extractedFiles.map(file => (
                <FilePreview key={file.path} {...file} />
              ))}
            </div>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
}

export default CodeExtractor;